import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchActiveRentals,
  fetchActiveAdminRentals,
  getAllSellers
} from '../../../Redux/Actions/actions';
import RentalCard from '../../../Components/RentalCard/RentalCard';
import RentalFilters from '../../../Components/RentalFilters/RentalFilters';
import NewContractChooser from '../NewContractChooser/NewContractChooser';
import { RENTAL_TYPE } from '../utils/rentalTypeHelpers';
import style from './ActiveContracts.module.css';

// Convierte un admin rental al shape esperado por RentalCard:
// { id, title, street, number, locality, province, propertyType, environments,
//   rooms, bathrooms, propertyState, ownerName, sellerName, currency, price,
//   rental: { ...campos del contrato } }
const adaptAdminRental = (item) => {
  if (!item) return null;
  const property = item.property || {};
  return {
    id: property.id || item.propertyId || item.id,
    title: property.title || item.propertyTitle || `Solo Admin #${item.id}`,
    street: property.street || item.propertyStreet || '',
    number: property.number || item.propertyNumber || '',
    locality: property.locality || item.propertyLocality || '',
    province: property.province || item.propertyProvince || '',
    propertyType: property.propertyType || item.propertyType || 'No especificado',
    environments: property.environments ?? item.environments ?? '—',
    rooms: property.rooms ?? item.rooms ?? '—',
    bathrooms: property.bathrooms ?? item.bathrooms ?? '—',
    propertyState: property.propertyState || item.propertyState || '—',
    ownerName: item.ownerName || property.ownerName,
    ownerBankAccount: item.ownerBankAccount,
    ownerBankAlias: item.ownerBankAlias,
    propertyReference: property.reference || item.propertyReference || '',
    sellerName: item.sellerName,
    sellerId: item.sellerId,
    currency: item.currency,
    price: item.currentRentValue || item.rentValue,
    // Espejo del contrato dentro de `rental` (lo que RentalCard lee)
    rental: {
      id: item.id,
      contractStatus: item.contractStatus || (item.isActive ? 'active' : 'pending'),
      contractDuration: item.contractDuration,
      isActive: item.isActive,
      currentRentValue: item.currentRentValue,
      rentValue: item.rentValue,
      currency: item.currency,
      depositAmount: item.depositAmount,
      depositCurrency: item.depositCurrency,
      expensesIncluded: item.expensesIncluded,
      expensesAmount: item.expensesAmount,
      paymentDueDay: item.paymentDueDay,
      lateInterestRate: item.lateInterestRate,
      gracePeriodDays: item.gracePeriodDays,
      contractStart: item.contractStart || item.startDate,
      contractEnd: item.contractEnd || item.endDate,
      nextAdjustmentDate: item.nextAdjustmentDate,
      createdAt: item.createdAt,
      adjustmentType: item.adjustmentType,
      adjustmentFrequency: item.adjustmentFrequency,
      adjustmentPercentage: item.adjustmentPercentage,
      ownerName: item.ownerName,
      ownerPhone: item.ownerPhone,
      ownerEmail: item.ownerEmail,
      ownerBankAccount: item.ownerBankAccount,
      ownerBankAlias: item.ownerBankAlias,
      propertyReference: item.propertyReference || property.reference,
      terminationDate: item.terminationDate,
      terminationReason: item.terminationReason,
      notes: item.notes,
      specialClauses: item.specialClauses,
      hasSignedContract: item.hasSignedContract,
      contractSignedDate: item.contractSignedDate,
      documentRent: item.documentRent,
      tenants: Array.isArray(item.tenants) ? item.tenants : [],
      payments: Array.isArray(item.payments) ? item.payments : undefined,
      lastPayment: item.lastPayment || item.lastPaidPayment
    }
  };
};

const ActiveContracts = () => {
  const dispatch = useDispatch();
  const {
    activeRentals,
    activeAdminRentals,
    rentalsLoading,
    adminRentalsLoading,
    rentalsError,
    adminRentalsError,
    sellers
  } = useSelector((state) => state);

  // Filtros
  const [filters, setFilters] = useState({
    status: 'active',
    page: 1
  });
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all'); // all | regular | admin
  const [showNewContract, setShowNewContract] = useState(false);

  const [sellersData, setSellersData] = useState({});
  const [loadingSellers, setLoadingSellers] = useState(false);

  // Lista de agentes para el filtro
  useEffect(() => {
    dispatch(getAllSellers('active'));
  }, [dispatch]);

  // Cargar contratos cuando cambien los filtros
  useEffect(() => {
    dispatch(fetchActiveRentals(filters));
    dispatch(
      fetchActiveAdminRentals({
        status: 'active',
        sellerId: filters.sellerId
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    filters.page,
    filters.status,
    filters.sellerId,
    filters.propertyId,
    filters.currency,
    filters.dateFrom,
    filters.dateTo
  ]);

  // Cargar datos de los sellers
  useEffect(() => {
    const loadSellersData = async () => {
      const all = [
        ...(activeRentals || []),
        ...(activeAdminRentals || [])
      ];
      if (all.length === 0) return;

      setLoadingSellers(true);

      const sellerIds = [
        ...new Set(
          all.filter((r) => r.sellerId).map((r) => r.sellerId)
        )
      ];

      const sellersMap = {};
      for (const sellerId of sellerIds) {
        try {
          const response = await fetch(
            `https://server.byraices.com/seller/${sellerId}`
          );
          const data = await response.json();
          if (data.success && data.data) {
            sellersMap[sellerId] = data.data;
          }
        } catch (error) {
          console.error(`Error al cargar seller ${sellerId}:`, error);
        }
      }

      setSellersData(sellersMap);
      setLoadingSellers(false);
    };

    loadSellersData();
  }, [activeRentals, activeAdminRentals]);

  // Lista combinada con _type
  const combinedRentals = useMemo(() => {
    const regulars = (activeRentals || []).map((rental) => ({
      _type: RENTAL_TYPE.REGULAR,
      data: rental
    }));
    const admins = (activeAdminRentals || []).map((item) => ({
      _type: RENTAL_TYPE.ADMIN,
      data: adaptAdminRental(item)
    }));
    return [...regulars, ...admins];
  }, [activeRentals, activeAdminRentals]);

  // Filtrar por tipo + búsqueda
  const filteredRentals = useMemo(() => {
    return combinedRentals.filter(({ _type, data }) => {
      if (typeFilter !== 'all' && _type !== typeFilter) return false;
      if (search) {
        const term = search.toLowerCase();
        const seller = data?.sellerId ? sellersData[data.sellerId] : null;
        const sellerFullName = seller
          ? `${seller.name} ${seller.last_name}`.toLowerCase()
          : '';
        const matches =
          data.title?.toLowerCase().includes(term) ||
          data.street?.toLowerCase().includes(term) ||
          data.locality?.toLowerCase().includes(term) ||
          data.ownerName?.toLowerCase().includes(term) ||
          data.rental?.tenants?.some((t) =>
            t.fullName?.toLowerCase().includes(term)
          ) ||
          sellerFullName.includes(term);
        return Boolean(matches);
      }
      return true;
    });
  }, [combinedRentals, typeFilter, search, sellersData]);

  const propertiesFromRentals = useMemo(() => {
    const map = new Map();
    combinedRentals.forEach(({ data }) => {
      if (data?.id && !map.has(data.id)) {
        map.set(data.id, {
          id: data.id,
          title: data.title,
          street: data.street,
          number: data.number,
          locality: data.locality
        });
      }
    });
    return Array.from(map.values());
  }, [combinedRentals]);

  const isLoading = rentalsLoading || adminRentalsLoading;
  const errorMessage =
    rentalsError && adminRentalsError ? rentalsError : null;

  return (
    <div className={style.container}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 12
        }}
      >
        <h1 className={style.title}>Contratos de Alquiler</h1>
        <button
          type="button"
          onClick={() => setShowNewContract(true)}
          style={{
            background: '#3b82f6',
            color: '#fff',
            border: 'none',
            padding: '10px 16px',
            borderRadius: 8,
            cursor: 'pointer',
            fontWeight: 600
          }}
        >
          + Nuevo Contrato
        </button>
      </div>

      <RentalFilters
        value={filters}
        onChange={setFilters}
        onClear={() => setFilters({ status: 'active', page: 1 })}
        sellers={sellers || []}
        properties={propertiesFromRentals}
      />

      <div className={style.filters}>
        <div className={style.filterGroup}>
          <label>Buscar:</label>
          <input
            type="text"
            placeholder="Propiedad, dirección, propietario o agente..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={style.searchInput}
          />
        </div>
        <div className={style.filterGroup}>
          <label>Tipo:</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {[
              { v: 'all', l: 'Todos' },
              { v: RENTAL_TYPE.REGULAR, l: 'Regular' },
              { v: RENTAL_TYPE.ADMIN, l: 'Solo Admin' }
            ].map((opt) => (
              <button
                key={opt.v}
                type="button"
                onClick={() => setTypeFilter(opt.v)}
                style={{
                  padding: '6px 12px',
                  borderRadius: 999,
                  border: 'none',
                  cursor: 'pointer',
                  background:
                    typeFilter === opt.v ? '#1e40af' : '#e5e7eb',
                  color: typeFilter === opt.v ? '#fff' : '#374151',
                  fontWeight: 600,
                  fontSize: '0.85rem'
                }}
              >
                {opt.l}
              </button>
            ))}
          </div>
        </div>
      </div>

      {isLoading && (
        <div className={style.loading}>Cargando contratos...</div>
      )}
      {loadingSellers && !isLoading && combinedRentals.length > 0 && (
        <div className={style.loading}>
          Cargando información de agentes...
        </div>
      )}
      {errorMessage && (
        <div className={style.error}>Error: {errorMessage}</div>
      )}

      {!isLoading && !errorMessage && (
        <>
          <div className={style.contractsList}>
            {filteredRentals.length === 0 ? (
              <div className={style.noData}>
                No se encontraron contratos con los filtros aplicados
              </div>
            ) : (
              filteredRentals.map(({ _type, data }) => {
                const rentalWithSeller = {
                  ...data,
                  seller: data?.sellerId
                    ? sellersData[data.sellerId]
                    : null
                };
                return (
                  <RentalCard
                    key={`${_type}-${data.rental?.id || data.id}`}
                    rental={rentalWithSeller}
                    type={_type}
                  />
                );
              })
            )}
          </div>

          <div className={style.stats}>
            <p>Total de contratos: {filteredRentals.length}</p>
          </div>
        </>
      )}

      {showNewContract && (
        <NewContractChooser
          onClose={() => setShowNewContract(false)}
          onSuccess={() => {
            setShowNewContract(false);
            dispatch(fetchActiveRentals(filters));
            dispatch(
              fetchActiveAdminRentals({
                status: 'active',
                sellerId: filters.sellerId
              })
            );
          }}
        />
      )}
    </div>
  );
};

export default ActiveContracts;
